import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Save, Plus, Trash2, Building2, Smartphone, Banknote } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });


// Tax rates offered on the invoice form. Invoices keep their own copy of the
// rate they were raised on, so editing or removing one here only changes what
// staff are offered next — issued documents are untouched.
function TaxSettings({ canEdit }) {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', rate: '', description: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/tax-rates`, authHeaders());
      setRates(res.data || []);
    } catch (error) {
      toast.error('Failed to load tax rates');
    } finally {
      setLoading(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const rate = parseFloat(form.rate);
    if (!form.name.trim()) return toast.error('Give the tax a name');
    if (Number.isNaN(rate) || rate < 0 || rate > 100) return toast.error('Rate must be between 0 and 100 percent');
    setBusy(true);
    try {
      await axios.post(`${API}/tax-rates`, {
        name: form.name.trim(),
        rate,
        description: form.description.trim() || null,
        is_default: rates.length === 0
      }, authHeaders());
      setForm({ name: '', rate: '', description: '' });
      toast.success('Tax rate added');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add tax rate');
    } finally {
      setBusy(false);
    }
  };

  const patch = async (tax, update, message) => {
    try {
      await axios.put(`${API}/tax-rates/${tax.id}`, update, authHeaders());
      if (message) toast.success(message);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tax rate');
    }
  };

  const remove = async (tax) => {
    try {
      await axios.delete(`${API}/tax-rates/${tax.id}`, authHeaders());
      toast.success(`${tax.name} removed`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove tax rate');
    }
  };

  return (
    <Card className="max-w-2xl mt-6" data-testid="tax-settings">
      <CardHeader>
        <CardTitle>Tax Configuration</CardTitle>
        <p className="text-sm text-slate-600">
          Rates offered when raising an invoice. The default is what a new invoice starts on —
          staff can switch to another rate or issue the invoice with no tax at all.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-6">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tax</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>On the invoice form</TableHead>
                  {canEdit && <TableHead className="w-10"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 4 : 3} className="text-center text-slate-500">
                      No tax configured — invoices are raised without tax until you add a rate.
                    </TableCell>
                  </TableRow>
                ) : (
                  rates.map((tax) => (
                    <TableRow key={tax.id} data-testid={`tax-row-${tax.id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{tax.name}</div>
                        {tax.description && <div className="text-xs text-slate-500">{tax.description}</div>}
                      </TableCell>
                      <TableCell className="font-medium">{tax.rate}%</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {tax.is_default ? (
                            <Badge className="bg-blue-100 text-blue-800" variant="outline">Default</Badge>
                          ) : canEdit && tax.is_active !== false ? (
                            <button type="button" className="text-xs text-blue-600 hover:underline"
                              onClick={() => patch(tax, { is_default: true }, `${tax.name} is now the default`)}>
                              Make default
                            </button>
                          ) : null}
                          <Switch
                            checked={tax.is_active !== false}
                            disabled={!canEdit}
                            data-testid={`tax-active-${tax.id}`}
                            onCheckedChange={(on) => patch(tax, { is_active: on },
                              on ? `${tax.name} offered on invoices` : `${tax.name} hidden from invoices`)}
                          />
                          <span className="text-xs text-slate-500">
                            {tax.is_active !== false ? 'Offered' : 'Hidden'}
                          </span>
                        </div>
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <Button type="button" variant="ghost" size="icon" onClick={() => remove(tax)}
                            data-testid={`tax-delete-${tax.id}`}>
                            <Trash2 size={16} />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {canEdit ? (
              <form onSubmit={add} className="flex flex-wrap gap-2 items-end border-t pt-4">
                <div className="flex-1 min-w-[10rem] space-y-1">
                  <Label htmlFor="tax-name" className="text-xs text-slate-600">Tax name</Label>
                  <Input id="tax-name" data-testid="tax-name-input" placeholder="e.g. VAT"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="w-24 space-y-1">
                  <Label htmlFor="tax-rate" className="text-xs text-slate-600">Rate %</Label>
                  <Input id="tax-rate" data-testid="tax-rate-input" type="number" step="0.01" min="0" max="100"
                    placeholder="18" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} />
                </div>
                <div className="flex-1 min-w-[10rem] space-y-1">
                  <Label htmlFor="tax-description" className="text-xs text-slate-600">Description</Label>
                  <Input id="tax-description" data-testid="tax-description-input" placeholder="e.g. Standard rate"
                    value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <Button type="submit" disabled={busy} data-testid="add-tax-button">
                  {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
                  Add rate
                </Button>
              </form>
            ) : (
              <p className="text-xs text-slate-500 border-t pt-4">You do not have permission to change tax settings.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


const MODE_ICONS = { bank_transfer: Building2, mobile_money: Smartphone, cheque: Banknote, cash: Banknote, other: Banknote };

const emptyAccount = {
  mode: 'bank_transfer', label: '', provider: '', account_name: '',
  account_number: '', branch: '', swift_code: '', qr_payload: '', instructions: ''
};

// A bank account and a Lipa Namba are not described by the same fields, so the
// form asks only for what the chosen mode actually has.
const MODE_FIELDS = {
  bank_transfer: [
    ['provider', 'Bank', 'e.g. CRDB Bank Tanzania'],
    ['account_name', 'Account name', 'e.g. Himan Investment & Company'],
    ['account_number', 'Account number', 'e.g. 0150123456789'],
    ['branch', 'Branch', 'e.g. Mwanza'],
    ['swift_code', 'SWIFT code', 'e.g. CORUTZTZ'],
  ],
  mobile_money: [
    ['provider', 'Provider', 'e.g. Vodacom M-Pesa'],
    ['account_name', 'Business name', 'e.g. HIMAN INVESTMENT'],
    ['account_number', 'Lipa Namba', 'e.g. 4001234'],
    ['qr_payload', 'QR content (optional)', 'Leave blank to encode the Lipa Namba'],
  ],
  cheque: [
    ['account_name', 'Cheque payable to', 'e.g. Himan Investment & Company'],
  ],
  cash: [],
  other: [
    ['provider', 'Provider', ''],
    ['account_name', 'Account name', ''],
    ['account_number', 'Reference number', ''],
  ],
};

// The company's own accounts — where customers send money. Invoices take a copy
// of whichever accounts they are issued with, so editing one here changes what
// future documents show, never what has already been issued.
function PaymentAccountSettings({ canEdit }) {
  const [accounts, setAccounts] = useState([]);
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyAccount);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [accountsRes, modesRes] = await Promise.all([
        axios.get(`${API}/payment-accounts`, authHeaders()),
        axios.get(`${API}/payment-modes`, authHeaders()),
      ]);
      setAccounts(accountsRes.data || []);
      setModes(modesRes.data || []);
    } catch (error) {
      toast.error('Failed to load payment accounts');
    } finally {
      setLoading(false);
    }
  };

  const modeLabel = (value) => modes.find((m) => m.value === value)?.label || value;

  const add = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error('Give the account a name staff will recognise');
    setBusy(true);
    try {
      await axios.post(`${API}/payment-accounts`, {
        ...form,
        is_default: accounts.length === 0,
        sort_order: accounts.length,
      }, authHeaders());
      setForm(emptyAccount);
      setShowForm(false);
      toast.success('Payment account added');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payment account');
    } finally {
      setBusy(false);
    }
  };

  const patch = async (account, update, message) => {
    try {
      await axios.put(`${API}/payment-accounts/${account.id}`, update, authHeaders());
      if (message) toast.success(message);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment account');
    }
  };

  const remove = async (account) => {
    try {
      await axios.delete(`${API}/payment-accounts/${account.id}`, authHeaders());
      toast.success(`${account.label} removed`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove payment account');
    }
  };

  const field = (key, label, placeholder) => (
    <div className="space-y-1">
      <Label htmlFor={`account-${key}`} className="text-xs text-slate-600">{label}</Label>
      <Input id={`account-${key}`} data-testid={`account-${key}-input`} placeholder={placeholder}
        value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </div>
  );

  return (
    <Card className="max-w-2xl mt-6" data-testid="payment-account-settings">
      <CardHeader>
        <CardTitle>Payment Accounts</CardTitle>
        <p className="text-sm text-slate-600">
          The company accounts customers pay into. Staff pick which ones an invoice shows, and the
          invoice keeps its own copy of the details — so correcting an account here never rewrites a
          document already issued.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-6">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            {accounts.length === 0 ? (
              <p className="text-sm text-slate-500 py-2">
                No payment accounts yet — invoices go out without payment details until you add one.
              </p>
            ) : (
              <div className="space-y-2">
                {accounts.map((account) => {
                  const Icon = MODE_ICONS[account.mode] || Banknote;
                  return (
                    <div key={account.id} className="border rounded-md p-3" data-testid={`account-row-${account.id}`}>
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex gap-3">
                          <Icon size={18} className="text-slate-500 mt-1" />
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-slate-900">{account.label}</span>
                              <Badge variant="outline" className="bg-slate-50 text-slate-600">{modeLabel(account.mode)}</Badge>
                              {account.is_default && <Badge variant="outline" className="bg-blue-100 text-blue-800">Default</Badge>}
                            </div>
                            <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                              {account.provider && <div>{account.provider}{account.branch ? ` · ${account.branch}` : ''}</div>}
                              {account.account_name && <div>{account.account_name}</div>}
                              {account.account_number && <div className="font-mono">{account.account_number}</div>}
                              {account.swift_code && <div>SWIFT {account.swift_code}</div>}
                              {account.instructions && <div className="italic">{account.instructions}</div>}
                            </div>
                            {account.qr_url && (
                              <img src={`${BACKEND_URL}${account.qr_url}`} alt={`${account.label} QR code`}
                                className="mt-2 h-24 w-24 border rounded" data-testid={`account-qr-${account.id}`} />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          {canEdit && !account.is_default && account.is_active !== false && (
                            <button type="button" className="text-xs text-blue-600 hover:underline"
                              onClick={() => patch(account, { is_default: true }, `${account.label} is now the default`)}>
                              Make default
                            </button>
                          )}
                          <Switch
                            checked={account.is_active !== false}
                            disabled={!canEdit}
                            data-testid={`account-active-${account.id}`}
                            onCheckedChange={(on) => patch(account, { is_active: on },
                              on ? `${account.label} offered on invoices` : `${account.label} hidden from invoices`)}
                          />
                          {canEdit && (
                            <Button type="button" variant="ghost" size="icon" onClick={() => remove(account)}
                              data-testid={`account-delete-${account.id}`}>
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {canEdit ? (
              showForm ? (
                <form onSubmit={add} className="border-t pt-4 space-y-3" data-testid="add-account-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-600">Payment mode</Label>
                      <Select value={form.mode}
                        onValueChange={(value) => setForm({ ...emptyAccount, mode: value, label: form.label })}>
                        <SelectTrigger data-testid="account-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {modes.map((mode) => (
                            <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {field('label', 'Name staff will see', form.mode === 'mobile_money' ? 'e.g. M-Pesa Lipa Namba' : 'e.g. CRDB — main account')}
                    {(MODE_FIELDS[form.mode] || []).map(([key, label, placeholder]) => (
                      <React.Fragment key={key}>{field(key, label, placeholder)}</React.Fragment>
                    ))}
                    {field('instructions', 'Note on the invoice', 'e.g. Quote the invoice number as reference')}
                  </div>
                  {form.mode === 'mobile_money' && (
                    <p className="text-xs text-slate-500">
                      A QR code is generated for this account and printed on invoices that carry it, so
                      customers can scan instead of typing the number.
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button type="submit" disabled={busy} data-testid="save-account-button">
                      {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
                      Add account
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => { setShowForm(false); setForm(emptyAccount); }}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="border-t pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowForm(true)} data-testid="add-account-button">
                    <Plus size={16} className="mr-2" /> Add payment account
                  </Button>
                </div>
              )
            ) : (
              <p className="text-xs text-slate-500 border-t pt-4">You do not have permission to change payment accounts.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// What every new invoice starts with, so terms are not retyped (or forgotten)
// on each one. An invoice that carries no terms of its own prints these.
function InvoiceDefaultSettings({ canEdit }) {
  const [terms, setTerms] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/settings/invoice-defaults`, authHeaders());
        setTerms(res.data?.payment_terms || '');
      } catch (error) {
        toast.error('Failed to load invoice defaults');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/invoice-defaults`, { payment_terms: terms }, authHeaders());
      setTerms(res.data?.payment_terms || '');
      toast.success('Invoice defaults saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice defaults');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="max-w-2xl mt-6" data-testid="invoice-default-settings">
      <CardHeader>
        <CardTitle>Invoice Defaults</CardTitle>
        <p className="text-sm text-slate-600">
          Payment terms every new invoice starts with. Staff can still change them on any single
          invoice, and an invoice raised without terms of its own prints these.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-6">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="default-terms" className="text-xs text-slate-600">Payment terms</Label>
              <Input id="default-terms" data-testid="default-terms-input" disabled={!canEdit}
                placeholder="e.g. Payment due within 30 days of invoice date"
                value={terms} onChange={(e) => setTerms(e.target.value)} />
            </div>
            {canEdit ? (
              <Button onClick={save} disabled={saving} data-testid="save-invoice-defaults-button">
                {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                Save
              </Button>
            ) : (
              <p className="text-xs text-slate-500">You do not have permission to change these settings.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Settings({ user }) {
  const [fields, setFields] = useState([]);
  const [required, setRequired] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const privileges = user?.privileges || [];
  const canEdit = user?.role?.toLowerCase() === 'admin' || privileges.includes('*') || privileges.includes('settings');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/customer-fields`, authHeaders());
      setFields(res.data?.fields || []);
      setRequired(res.data?.required || []);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (key, on) => {
    setRequired(on ? [...required, key] : required.filter((f) => f !== key));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/customer-fields`, { required }, authHeaders());
      setRequired(res.data?.required || []);
      toast.success('Onboarding settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-600 mt-1">System configuration</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Customer Onboarding — Required Fields</CardTitle>
          <p className="text-sm text-slate-600">
            Turn a field on to make it mandatory when onboarding or editing a customer.
            Everything left off can be saved blank and filled in later.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-6">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-1">
              {fields.map((field) => (
                <div key={field.key} className="flex items-center justify-between py-3 border-b last:border-0">
                  <Label htmlFor={`field-${field.key}`} className="font-normal text-slate-700">
                    {field.label}
                  </Label>
                  <Switch
                    id={`field-${field.key}`}
                    data-testid={`required-${field.key}`}
                    checked={required.includes(field.key)}
                    disabled={!canEdit}
                    onCheckedChange={(on) => toggle(field.key, on)}
                  />
                </div>
              ))}
              {canEdit ? (
                <div className="pt-4">
                  <Button onClick={save} disabled={saving} data-testid="save-settings-button">
                    {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                    Save Settings
                  </Button>
                  {required.length === 0 && (
                    <p className="text-xs text-slate-500 mt-2">
                      No field is mandatory — staff can onboard a customer with whatever details they have.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500 pt-4">You do not have permission to change these settings.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <TaxSettings canEdit={canEdit} />

      <PaymentAccountSettings canEdit={canEdit} />

      <InvoiceDefaultSettings canEdit={canEdit} />
    </div>
  );
}
